import useAxiosInterceptor from 'src/services/interceptor';
import { documentLibrary, SIGNING_PIN_HEADER } from '../constants';

type GetAllSubmittedDocuments = {
  therapistUuid: string;
  eocId: string;
  params: object;
  signal: any;
};
class TemplateService {
  private axiosInstance;

  constructor() {
    // eslint-disable-next-line react-hooks/rules-of-hooks -- factory, not a React hook (matches npcLogService)
    this.axiosInstance = useAxiosInterceptor();
  }

  getAllTemplates = (payload: any) => {
    const signal = payload?.signal;
    if (payload?.signal) delete payload?.signal;

    return this.axiosInstance.get(documentLibrary.getAllTemplates, {
      params: { ...payload },
      signal,
    });
  };

  // THIN-7247: single-axis assignment endpoints replace the form-type-routed
  // `/save-submission-data?isSuper=true` path. The BE was dispatching by document type
  // (eval/recert → ASSIGN_PHYSICIAN, everything else → ASSIGN_SUPERVISOR), which
  // mis-routed supervisor assignments on eval/recert with supervision required.
  assignSupervisor = (params: { formSubmissionUuid: string; supervisorId: string }) => {
    return this.axiosInstance.post(documentLibrary.assignSupervisor, null, {
      params: { ...params },
    });
  };

  assignPhysician = (params: { formSubmissionUuid: string; physicianId: string }) => {
    return this.axiosInstance.post(documentLibrary.assignPhysician, null, {
      params: { ...params },
    });
  };

  getSubmittedDocuments = (payload: GetAllSubmittedDocuments) => {
    return this.axiosInstance.get(documentLibrary.getAllSubmittedDocuments(payload.eocId), {
      params: {
        ...payload.params,
      },
      signal: payload?.signal,
    });
  };

  getCompetedDocument = (payload: GetAllSubmittedDocuments) => {
    return this.axiosInstance.get(documentLibrary.getAllSubmittedDocuments(payload.eocId), {
      params: {
        ...payload.params,
      },
      signal: payload?.signal,
    });
  };

  getSubmissionData = (submissionId: string) => {
    return this.axiosInstance.get(`${documentLibrary.getDataBySubmissionId}/${submissionId}`);
  };

  getSubmissionDocument = (payload: { submissionId: string; showAuditTrail: boolean }) => {
    return this.axiosInstance.get(documentLibrary.getSubmissionDocument(payload.submissionId), {
      params: { showAuditTrail: payload.showAuditTrail },
    });
  };

  getSignatureTemplate = (payload: object) => {
    return this.axiosInstance.put(documentLibrary.getSignatureTemplate, null, {
      params: { ...payload },
    });
  };

  getAllMiscellaneousDocuments = (payload: any) => {
    return this.axiosInstance.get(
      `${documentLibrary.getAllMiscellaneousDocuments}/${payload?.EocId}`,
      {
        params: payload?.params,
      }
    );
  };

  saveMiscellaneousDocument = (payload: any) => {
    return this.axiosInstance.post(documentLibrary.saveMiscellaneousDocument, payload?.data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params: {
        ...payload?.param,
      },
    });
  };

  getAllSupervisiorsByDiscipline = (payload: object) => {
    return this.axiosInstance.get(documentLibrary.getAllSupervisiors, {
      params: {
        ...payload,
      },
    });
  };

  sendMailToPhysician = (supervisiorUuid: string, payload: object) => {
    return this.axiosInstance.put(
      `${documentLibrary.sendMailToPhysician}/${supervisiorUuid}`,
      null,
      {
        params: { ...payload },
      }
    );
  };

  getCPTorICDcodes = (payload: any, patientUuid: string) => {
    const signal = payload?.signal;
    if (payload?.signal) delete payload?.signal;
    return this.axiosInstance.get(documentLibrary.getCodes(patientUuid), {
      params: { ...payload },
      signal: signal,
    });
  };

  getSignatureAccess = (payload: Array<string>, params: object) => {
    return this.axiosInstance.post(documentLibrary.getSignatureAccess, payload, {
      params: { ...params },
    });
  };

  pullUuid = (payload: object, params = {}) => {
    return this.axiosInstance.post(documentLibrary.pullUuid, payload, {
      params,
    });
  };

  reAssignTherapist = (documentUuid: string, providerUuid: string) => {
    return this.axiosInstance.put(documentLibrary.reAssignTherapist(documentUuid, providerUuid));
  };

  getAllTherapist = (
    discipline: string | null | undefined,
    role: string | null | undefined,
    therapistId: string | null | undefined
  ) => {
    return this.axiosInstance.get(documentLibrary.getAllProviderList(), {
      params: {
        discipline,
        role,
        therapistId,
      },
    });
  };

  deleteRequireddocument = (documentUuid: string) => {
    return this.axiosInstance.delete(documentLibrary.deleteRequiredDocument(documentUuid));
  };

  getPrepareProgressNote = (episodeOfCareId: string, params: object) => {
    return this.axiosInstance.get(documentLibrary.prepareProgressNote(episodeOfCareId), {
      params,
    });
  };

  checkDischargeDocumentAllow = (payload: any) => {
    return this.axiosInstance.get(documentLibrary.checkDischargeDoc(payload?.eocId), {
      params: payload?.params,
    });
  };

  checkPendingDocsAlert = (payload: any) => {
    return this.axiosInstance.get(documentLibrary.checkPendingDocs(payload?.eocId), {
      params: payload?.params,
    });
  };

  recheckDischargeOverrideStatus = () => {
    return this.axiosInstance.get(documentLibrary.recheckDischargeOverrideStatus());
  };

  approveRecheckDischarge = (payload: any) => {
    return this.axiosInstance.post(documentLibrary.approveRecheckDischarge(payload?.eocId), null, {
      params: { discipline: payload?.discipline },
    });
  };

  newDocSignatureFlow = (payload: any) => {
    //  THIN-7775: the signing PIN travels in SIGNING_PIN_HEADER, never the query string — pull it out
    //  of the payload here so a ?pin= can't reach the ALB access log or Datadog's http.url.
    const { pin, ...params } = payload ?? {};
    return this.axiosInstance.post(`provider/signature/sign-document`, null, {
      params,
      headers: {
        'X-Forwarded-For': localStorage.getItem('ip-address') ?? '',
        ...(pin ? { [SIGNING_PIN_HEADER]: pin } : {}),
      },
    });
  };

  undoDocSignatureFlow = (payload: any) => {
    return this.axiosInstance.post(`provider/signature/unsign-document`, null, {
      params: { ...payload },
      headers: {
        'X-Forwarded-For': localStorage.getItem('ip-address') ?? '',
      },
    });
  };
  getDocDueSettings = (payload: any) => {
    return this.axiosInstance.get(documentLibrary.getDocDueSettings(), {
      params: payload?.params,
    });
  };

  getTherapistInfo = (payload: any) => {
    return this.axiosInstance.get(`/provider/get-therapist-info/${payload?.providerId}`);
  };

  signOnBehalf = (payload: any) => {
    //  THIN-7775: no PIN on this path today (see the proxy-sign follow-up), but strip defensively
    //  so a caller adding one can't put it back in the query string.
    const { pin, ...params } = payload ?? {};
    return this.axiosInstance.post(`/provider/signature/sign-document`, null, {
      params,
      headers: {
        'X-Forwarded-For': localStorage.getItem('ip-address') ?? '',
        ...(pin ? { [SIGNING_PIN_HEADER]: pin } : {}),
      },
    });
  };
}

const templateService = new TemplateService();

Object.freeze(templateService);

export default templateService;
